// ownerSync — wire shapes + PURE logic for the AppFolio owner / vendor sync
// surfaces (docs/appfolio-owner-vendor-sync-spec.md §3, §6, §7, §12).
//
// PURE ON PURPOSE, like lib/requestsKinds.ts: the only import is the id regex
// from lib/noIds.ts (itself import-free), so nothing in this module's graph
// reads `import.meta.env` and every rule here can be asserted with a bare
// `npx tsx --test src/lib/ownerSync.test.ts`. The fetchers live in
// lib/ownerSyncClient.ts, which re-exports these types.
//
// ⚠️ NO IDS ON SCREEN (docs/no-ids-in-ui.md). `appfolioOwnerId`,
// `appfolioVendorId` and `opKey` ride in REQUESTS only — every string this
// module builds for display is made of names, emails, cities and plain words.

import { OPAQUE_ID_RE } from './noIds';

// ---------------------------------------------------------------------------
// Wire shapes — copied verbatim from spec §12 (the build contract). Do not
// reshape them here; the API workstreams build to the same text.
// ---------------------------------------------------------------------------

export type AppFolioSyncStatus =
  | 'pending'
  | 'blocked_no_owner_id'
  // The signed City utilities agreement waiting for its PROPERTY to exist in
  // AppFolio (docs/city-utility-agreement-plan.md §6.2).
  | 'blocked_no_property_id'
  | 'sent'
  | 'succeeded'
  | 'failed'
  | 'stuck';

/**
 * 'run' = the normal step; 'skipped_existing' = not sent (existing owner, §3);
 * 'run_contact_only' = the existing-owner OVERRIDE — the Owner Contact intake goes
 * out WITHOUT its banking / year-end tax section (§13). Only used for `addendum`.
 */
export type OwnerChainStep = 'run' | 'skipped_existing' | 'run_contact_only';

/** §3: decided at initiate; returned on the initiate response. */
export interface OwnerChainPlan {
  addendum: OwnerChainStep;
  /** Keyed by EPMA roster role (Owner{n}). A role absent here = 'run'. */
  w9: Record<string, OwnerChainStep>;
  decidedAt: string;
}

export interface OwnerMatch {
  /** Used in requests only — NEVER rendered. */
  appfolioOwnerId: string;
  displayName: string;
  isCompany: boolean;
  primaryEmail: string | null;
  city: string | null;
  state: string | null;
  /** The view's has_taxpayer_id; null = unknown (not in the 1099 report). */
  hasTaxpayerId: boolean | null;
  /** A Fully Executed W-9 with us for this owner. */
  hasCompletedW9: boolean;
}

export interface VendorMatch {
  /** NEVER rendered. */
  appfolioVendorId: string;
  displayName: string;
  isCompany: boolean;
  primaryEmail: string | null;
  hasTaxpayerId: boolean | null;
  /** An unsigned, uncancelled W-9 for this vendor exists. */
  openW9Request: boolean;
}

/**
 * What a step DOES, when the server says so. Not in §12 yet: `owner.upsert`
 * covers both a create and an update, and resolving a CREATE needs the
 * AppFolio owner it made, so the page must tell them apart. Until the server
 * sends `opKind`, the create is recognised by its label (isOwnerCreateOp).
 */
export type OwnerSyncOpKind =
  | 'owner.create'
  | 'owner.update'
  | 'owner.note'
  | 'owner.attach'
  | 'owner.set_tin'
  | 'vendor.set_tin'
  /** The signed City utilities agreement onto the PROPERTY (city-utility plan §6). */
  | 'property.attach';

export interface OwnerSyncOpView {
  /** For retry/resolve calls only — NEVER rendered. */
  opKey: string;
  /** Optional until the server exposes it — see OwnerSyncOpKind. */
  opKind?: OwnerSyncOpKind;
  /** Plain language: "Create owner in AppFolio", "Attach signed W-9", … */
  label: string;
  ownerName: string | null;
  status: AppFolioSyncStatus;
  /** "Done" · "Waiting" · "Waiting for owner to be created" · "Sending" · "Failed" · "Needs a check in AppFolio" */
  statusLabel: string;
  errorCode?: string;
  errorMessage?: string;
  attempt: number;
  updatedAt: string;
  /** failed | stuck — false for a stuck create/note/attach whose outcome is unknown (Retry would only replay it). */
  canRetry: boolean;
  /** failed | stuck */
  canResolve: boolean;
  /**
   * A stuck create / note / attach whose final outcome is unknown: the router's
   * recorded answer is final, so Retry can never finish it — staff check
   * AppFolio and, if it is not there, send it again as a fresh attempt
   * (POST /api/owner-sync/ops/resend-confirmed). Optional until workstream B
   * ships it; absent = false.
   */
  canResendConfirmed?: boolean;
  /**
   * A property attachment waiting for its AppFolio property: the page offers
   * "Link the AppFolio property" (POST /api/owner-intake/:groupId/link-property).
   * Absent = false.
   */
  canLinkProperty?: boolean;
}

/** GET /api/owner-sync/:engagementGroupId */
export interface OwnerSyncView {
  ops: OwnerSyncOpView[];
  appfolioOwnerLinked: boolean;
}

export type W9RecipientKind = 'owner' | 'vendor';

/** POST /api/w9-requests body. */
export interface W9RequestBody {
  recipientKind: W9RecipientKind;
  appfolioId?: string;
  displayName: string;
  email: string;
  isCompany?: boolean;
  replaceOpen?: boolean;
}

/**
 * What the POST came back as, folded into one union the page switches on.
 * §12 plus the codes workstream C's route adds (routes/w9Requests.ts): every
 * non-2xx carries a plain staff-facing `message`, kept here only when it
 * carries no id.
 */
export type W9RequestOutcome =
  | { kind: 'sent'; recipientName: string; replacedOpen: boolean }
  /** 409 — offer "Send a new one" (resend with replaceOpen). */
  | { kind: 'open_request_exists'; recipientName: string }
  /** 409 — with replaceOpen, the open W-9 was signed mid-replace; nothing new sent. */
  | { kind: 'open_request_completed'; recipientName: string }
  /** 409 — a send for this recipient is already running (a double click). */
  | { kind: 'in_flight'; recipientName: string }
  | {
      kind: 'rejected';
      error: 'staff_email' | 'vendor_requires_id' | 'unknown_recipient' | 'invalid';
      /** 400 invalid only: which field the server refused. */
      field?: string;
    }
  /** 503 disabled (or a bare 503): the flag for this kind is off. */
  | { kind: 'not_enabled'; message?: string }
  /** 503 directory_unavailable / template_unconfigured: set-up, not a flag. */
  | { kind: 'unavailable'; message?: string }
  /** 502 / anything else. `outcomeUnknown` = send_outcome_unknown: do not just retry. */
  | { kind: 'failed'; message: string; outcomeUnknown?: boolean };

// ---------------------------------------------------------------------------
// Small shared helpers
// ---------------------------------------------------------------------------

export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

/** "A", "A and B", "A, B and C". */
export function joinNames(names: string[]): string {
  const clean = names.map((n) => n.trim()).filter(Boolean);
  if (clean.length <= 1) return clean[0] ?? '';
  return `${clean.slice(0, -1).join(', ')} and ${clean[clean.length - 1]}`;
}

// ---------------------------------------------------------------------------
// §3 — existing-owner detection on the EPMA initiate form
// ---------------------------------------------------------------------------

/** "(jane@example.com, Walla Walla)" — the parenthetical after the name. Email + city only. */
export function ownerMatchDetail(m: Pick<OwnerMatch, 'primaryEmail' | 'city'>): string {
  const parts = [m.primaryEmail?.trim(), m.city?.trim()].filter((p): p is string => !!p);
  return parts.join(', ');
}

/** What the owner card should offer under the email field. */
export type OwnerMatchPrompt =
  | { kind: 'none' }
  | { kind: 'single'; match: OwnerMatch }
  | { kind: 'multiple'; matches: OwnerMatch[] };

export interface OwnerMatchRowState {
  /** The email as typed right now. */
  email: string;
  /** Set once staff linked the row — a linked row asks nothing more. */
  appfolioOwnerId?: string | undefined;
  /** The last lookup, keyed by the normalized email it ran for. */
  lookup?: { email: string; matches: OwnerMatch[] } | null | undefined;
  /** The normalized email staff answered "No, new owner" for. */
  dismissedEmail?: string | undefined;
}

/**
 * The prompt for one owner card. Nothing is offered when the row is already
 * linked, when the lookup is for a different email than the one now typed (a
 * stale answer), when staff already said "No, new owner" for this email, or
 * when there were no matches — the last is also exactly what a switched-off
 * OWNER_MATCH_ENABLED returns, so the form stays as it is today.
 */
export function ownerMatchPromptFor(row: OwnerMatchRowState): OwnerMatchPrompt {
  if (row.appfolioOwnerId) return { kind: 'none' };
  const lookup = row.lookup;
  if (!lookup) return { kind: 'none' };
  if (lookup.email !== normalizeEmail(row.email)) return { kind: 'none' };
  if (row.dismissedEmail && row.dismissedEmail === lookup.email) return { kind: 'none' };
  if (lookup.matches.length === 0) return { kind: 'none' };
  if (lookup.matches.length === 1) return { kind: 'single', match: lookup.matches[0]! };
  return { kind: 'multiple', matches: lookup.matches };
}

/** Split an AppFolio display name into first / last ("Doe, Jane" and "Jane Q. Doe" both work). */
export function splitDisplayName(name: string): { firstName: string; lastName: string } {
  const trimmed = name.trim();
  const comma = trimmed.indexOf(',');
  if (comma > 0) {
    const last = trimmed.slice(0, comma).trim();
    const first = trimmed.slice(comma + 1).trim();
    if (first && last) return { firstName: first, lastName: last };
  }
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return { firstName: parts[0] ?? '', lastName: '' };
  return { firstName: parts.slice(0, -1).join(' '), lastName: parts[parts.length - 1]! };
}

export interface OwnerNameFields {
  ownerType: 'individual' | 'entity';
  firstName: string;
  lastName: string;
  entityName: string;
}

/**
 * "Use this owner": link the row and take the NAME from AppFolio (staff can
 * still edit it — an entity's signer is a person AppFolio doesn't name, so the
 * signer fields are left as typed for a company match).
 */
export function applyOwnerMatch(
  row: OwnerNameFields,
  m: OwnerMatch,
): Partial<OwnerNameFields> & { appfolioOwnerId: string; linkedMatch: OwnerMatch } {
  if (m.isCompany) {
    return {
      appfolioOwnerId: m.appfolioOwnerId,
      linkedMatch: m,
      ownerType: 'entity',
      entityName: m.displayName.trim(),
    };
  }
  const { firstName, lastName } = splitDisplayName(m.displayName);
  return {
    appfolioOwnerId: m.appfolioOwnerId,
    linkedMatch: m,
    ownerType: 'individual',
    firstName: firstName || row.firstName,
    lastName: lastName || row.lastName,
  };
}

/** "Owner2" → "Owner 2"; the name when we have it. Never an id. */
function ownerLabelForRole(role: string, owners: Array<{ role: string; name: string }>): string {
  const name = owners.find((o) => o.role === role)?.name?.trim();
  if (name) return name;
  const n = /^Owner(\d+)$/.exec(role)?.[1];
  return n ? `Owner ${n}` : 'an owner';
}

/**
 * The one-line "what we skipped" after a send, from the initiate response's
 * chainPlan. Null when nothing was skipped (or the server sent no plan — the
 * flag is off), so today's confirmation stays exactly as it was.
 */
export function chainPlanSummary(
  plan: OwnerChainPlan | null | undefined,
  owners: Array<{ role: string; name: string }>,
): string | null {
  if (!plan) return null;
  const parts: string[] = [];
  if (plan.addendum === 'skipped_existing') {
    parts.push('Owner contact step skipped — existing owner.');
  } else if (plan.addendum === 'run_contact_only') {
    parts.push('Owner details form (contact only — no banking questions).');
  }
  const skippedW9 = Object.entries(plan.w9 ?? {})
    .filter(([, step]) => step === 'skipped_existing')
    .map(([role]) => role)
    .sort()
    .map((role) => ownerLabelForRole(role, owners));
  if (skippedW9.length > 0) {
    parts.push(`W-9 skipped for ${joinNames(skippedW9)} — already on file.`);
  }
  return parts.length > 0 ? parts.join(' ') : null;
}

// ---------------------------------------------------------------------------
// §7 — AppFolio sync status on the Owners page
// ---------------------------------------------------------------------------

export type PillTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

export function syncStatusTone(status: AppFolioSyncStatus): PillTone {
  switch (status) {
    case 'succeeded':
      return 'success';
    case 'failed':
      return 'danger';
    case 'stuck':
      return 'warning';
    case 'sent':
      return 'info';
    default:
      return 'neutral';
  }
}

/** The server's label for the owner-create step (routes → appfolioSyncOutbox.opLabel). */
export const CREATE_OWNER_LABEL = 'Create owner in AppFolio';

/**
 * Is this the step that CREATES the AppFolio owner? Marking it done by hand has
 * to say which owner it made (the server answers 409 owner_required without
 * one), or the note / attachments / taxpayer ID waiting on it are stranded.
 * `opKind` decides when the server sends it; the label is the fallback.
 */
export function isOwnerCreateOp(op: Pick<OwnerSyncOpView, 'label'> & { opKind?: OwnerSyncOpKind | undefined }): boolean {
  if (op.opKind) return op.opKind === 'owner.create';
  return op.label.trim() === CREATE_OWNER_LABEL;
}

/** The button that sends a stuck step again after staff checked AppFolio. */
export const RESEND_CONFIRMED_LABEL = 'Not in AppFolio — send again';

/** The hint for a stuck create whose outcome is unknown — check, then pick or send again. */
export const STUCK_CREATE_HINT = `Check AppFolio. If the owner is there, pick them to mark this step done. If not, use "${RESEND_CONFIRMED_LABEL}".`;

/**
 * What staff should do when a stuck step's change is NOT in AppFolio — driven
 * by the flags on the step, so the copy never names a button that isn't there:
 * "Not in AppFolio — send again" when the server offers it (an unknown final
 * outcome, where Retry only replays), else Retry, else nothing.
 */
export function notInAppFolioAction(
  op: { canRetry?: boolean | undefined; canResendConfirmed?: boolean | undefined },
): string | null {
  if (op.canResendConfirmed) return `use "${RESEND_CONFIRMED_LABEL}"`;
  if (op.canRetry) return 'use Retry';
  return null;
}

/**
 * A plain-language next step for a failed / stuck op, from its error CODE.
 * The router's `errorMessage` is deliberately NOT shown: it has digits
 * scrubbed but can still carry fragments of AppFolio ids.
 */
export function syncErrorHint(
  op: Pick<OwnerSyncOpView, 'status' | 'errorCode'> & {
    label?: string | undefined;
    opKind?: OwnerSyncOpKind | undefined;
    canRetry?: boolean | undefined;
    canResendConfirmed?: boolean | undefined;
  },
): string | null {
  if (op.status !== 'failed' && op.status !== 'stuck') return null;
  const create = op.label !== undefined && isOwnerCreateOp({ label: op.label, opKind: op.opKind });
  const onProperty = op.opKind === 'property.attach';
  switch (op.errorCode) {
    case 'file_too_large':
      return onProperty
        ? 'The file is too large for AppFolio. Attach it to the property by hand, then mark this done.'
        : 'The file is too large for AppFolio. Attach it to the owner by hand, then mark this done.';
    case 'invalid_request':
      if (onProperty) {
        return 'AppFolio could not take a document on a property through WHP yet. Retry once it can, or attach the signed form to the property by hand and mark this done.';
      }
      break;
    case 'invalid_tin':
      return 'AppFolio did not accept the taxpayer ID from the signed W-9. Check the W-9, then retry or enter it in AppFolio by hand.';
    case 'not_found':
      return onProperty
        ? 'AppFolio could not find the linked property. Link the right property, then retry.'
        : 'AppFolio could not find the owner record. Check the owner in AppFolio before retrying.';
    case 'appfolio_rejected':
      return 'AppFolio turned the change down. Check the owner in AppFolio before retrying.';
    default:
      break;
  }
  if (op.status === 'stuck' || op.errorCode === 'outcome_unknown') {
    const ifMissing = notInAppFolioAction(op);
    if (create) {
      if (op.canResendConfirmed) return STUCK_CREATE_HINT;
      return `Check AppFolio. If the owner is there, pick them to mark this step done.${
        ifMissing ? ` If not, ${ifMissing}.` : ''
      }`;
    }
    return `AppFolio may already have this change. Check the owner in AppFolio: if it is there, mark this done.${
      ifMissing ? ` If not, ${ifMissing}.` : ''
    }`;
  }
  return null;
}

/**
 * The words to show when Retry / Mark resolved / send-again is refused. The owner-sync
 * routes answer two ways — `{ error: '<a plain sentence>' }` (not found / not
 * actionable) and `{ error: '<machine_code>', message: '<plain sentence>' }`
 * (409 owner_required, 409 not_eligible) — so prefer `message`, accept `error` only when it reads
 * as a sentence, never show a bare code, and never show anything carrying an id.
 */
export function syncActionErrorMessage(body: unknown, fallback: string): string {
  const b = (body ?? {}) as { error?: unknown; message?: unknown };
  const usable = (v: unknown): string => {
    if (typeof v !== 'string') return '';
    const t = v.trim();
    if (!t || OPAQUE_ID_RE.test(t)) return '';
    return /\s/.test(t) ? t : ''; // a single token is a machine code, not words
  };
  const msg = usable(b.message) || usable(b.error);
  if (msg) return msg;
  if (b.error === 'owner_required') {
    return 'Pick the AppFolio owner this step created before marking it done.';
  }
  if (b.error === 'not_eligible') {
    return 'This step cannot be sent again any more. Refresh to see where it stands.';
  }
  return fallback;
}

/** The section only renders when there is something to show (404/503 → null upstream). */
export function syncOpsToShow(view: OwnerSyncView | null | undefined): OwnerSyncOpView[] {
  return view?.ops ?? [];
}

// ---------------------------------------------------------------------------
// City utilities agreement — "Link the AppFolio property" (city-utility plan §6.2)
// ---------------------------------------------------------------------------

/** The Owners-page action on a property attachment waiting for its property. */
export const LINK_PROPERTY_LABEL = 'Link the AppFolio property';

/** A property as the picker lists it (GET /api/properties — PropertyOption's shape). */
export interface LinkablePropertyOption {
  /** Used in the request only — NEVER rendered. */
  propertyId: string;
  name: string;
  address: string | null;
}

/** The street line of an address ("829 S 2nd Ave, Walla Walla, WA" → "829 S 2nd Ave") — the picker's seed. */
export function streetLineOf(address: string | null | undefined): string {
  return (address ?? '').split(',')[0]!.replace(/\s+/g, ' ').trim();
}

/**
 * The properties whose name or address contains every word of the query
 * (case-insensitive), at most `limit` of them. An empty query lists nothing — the
 * picker asks for a few letters first rather than dumping every property.
 */
export function propertyPickerMatches(
  properties: readonly LinkablePropertyOption[],
  query: string,
  limit = 25,
): LinkablePropertyOption[] {
  const words = query.toLowerCase().replace(/[.,#]/g, ' ').split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];
  const out: LinkablePropertyOption[] = [];
  for (const p of properties) {
    const hay = `${p.name} ${p.address ?? ''}`.toLowerCase().replace(/[.,#]/g, ' ');
    if (words.every((w) => hay.includes(w))) out.push(p);
    if (out.length >= limit) break;
  }
  return out;
}

/** One picker row: the property's name and address — never its id. */
export function propertyResultRow(p: LinkablePropertyOption): RecipientResultRow {
  const address = (p.address ?? '').trim();
  const rawName = (p.name ?? '').trim();
  const name = rawName && !OPAQUE_ID_RE.test(rawName) ? rawName : address || 'Unnamed property';
  return {
    key: p.propertyId,
    name,
    detail: address && address !== name ? address : '',
    badges: [],
  };
}

/** The link-property 409: the two addresses that disagree, in words. */
export interface LinkPropertyMismatch {
  message: string;
  intakeAddress: string;
  appfolioAddress: string;
}

/** Read a link-property 409 body (`error: 'address_mismatch'`), or null for anything else. */
export function linkPropertyMismatchFrom(body: unknown): LinkPropertyMismatch | null {
  const b = (body ?? {}) as { error?: unknown; message?: unknown; intakeAddress?: unknown; appfolioAddress?: unknown };
  if (b.error !== 'address_mismatch') return null;
  const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
  return {
    message:
      syncActionErrorMessage(body, '') ||
      "That AppFolio property's address does not match the address on this onboarding.",
    intakeAddress: str(b.intakeAddress),
    appfolioAddress: str(b.appfolioAddress),
  };
}

// ---------------------------------------------------------------------------
// §6 — standalone "Request a W-9"
// ---------------------------------------------------------------------------

/** Staff addresses are refused server-side (400 staff_email); say so before the round-trip. */
export function isStaffEmail(email: string): boolean {
  return /@welcomehps\.com$/i.test(email.trim());
}

/** Where the Owners page's "Request a W-9" goes, prefilled for that owner. */
export function w9RequestHref(p: { name?: string | null; email?: string | null }): string {
  const q = new URLSearchParams({ kind: 'owner' });
  if (p.name?.trim()) q.set('name', p.name.trim());
  if (p.email?.trim()) q.set('email', p.email.trim());
  return `/send/w9-request?${q.toString()}`;
}

export interface W9RequestPrefill {
  kind: W9RecipientKind;
  name: string;
  email: string;
}

/** Read `?kind=owner&name=&email=` (anything but `vendor` is an owner). */
export function w9PrefillFromSearch(params: URLSearchParams): W9RequestPrefill {
  return {
    kind: params.get('kind') === 'vendor' ? 'vendor' : 'owner',
    name: (params.get('name') ?? '').trim(),
    email: (params.get('email') ?? '').trim(),
  };
}

export interface RecipientBadge {
  text: string;
  tone: PillTone;
}

/** One search result, as the picker renders it — `key` addresses, the rest is read. */
export interface RecipientResultRow {
  key: string;
  name: string;
  detail: string;
  badges: RecipientBadge[];
}

function taxpayerBadge(hasTaxpayerId: boolean | null): RecipientBadge | null {
  if (hasTaxpayerId === true) return { text: 'Taxpayer ID on file', tone: 'success' };
  if (hasTaxpayerId === false) return { text: 'Taxpayer ID missing', tone: 'warning' };
  return null; // unknown — not in the 1099 report; say nothing rather than guess
}

export function ownerResultRow(m: OwnerMatch): RecipientResultRow {
  const place = [m.city?.trim(), m.state?.trim()].filter(Boolean).join(', ');
  const badges: RecipientBadge[] = [];
  if (m.hasCompletedW9) badges.push({ text: 'W-9 on file with us', tone: 'success' });
  else {
    const tin = taxpayerBadge(m.hasTaxpayerId);
    if (tin) badges.push(tin);
  }
  return {
    key: m.appfolioOwnerId,
    name: m.displayName,
    detail: [m.primaryEmail?.trim(), place].filter(Boolean).join(' · '),
    badges,
  };
}

export function vendorResultRow(v: VendorMatch): RecipientResultRow {
  const badges: RecipientBadge[] = [];
  const tin = taxpayerBadge(v.hasTaxpayerId);
  if (tin) badges.push(tin);
  if (v.openW9Request) badges.push({ text: 'A W-9 request is already open', tone: 'info' });
  return {
    key: v.appfolioVendorId,
    name: v.displayName,
    detail: v.primaryEmail?.trim() ?? '',
    badges,
  };
}

/** What the page shows after a submit — every POST outcome has exactly one of these. */
export interface W9OutcomeNotice {
  tone: 'success' | 'warning' | 'danger' | 'info';
  title: string;
  body: string;
  /** 409: offer "Send a new one — this cancels the open request" (resend with replaceOpen). */
  offerReplace: boolean;
}

export function w9OutcomeNotice(outcome: W9RequestOutcome, kind: W9RecipientKind): W9OutcomeNotice {
  const who = kind === 'vendor' ? 'vendor' : 'owner';
  switch (outcome.kind) {
    case 'sent':
      return {
        tone: 'success',
        title: `W-9 request sent to ${outcome.recipientName}`,
        body: `We emailed ${outcome.recipientName} a secure link to fill in and sign their W-9.${
          outcome.replacedOpen ? ' The earlier request was cancelled, so only this new link works.' : ''
        } Track it on the Requests page.`,
        offerReplace: false,
      };
    case 'open_request_exists':
      return {
        tone: 'warning',
        title: `${outcome.recipientName} already has an open W-9 request`,
        body: 'Their earlier link still works. Sending a new one cancels the open request, so only the newest link can be used.',
        offerReplace: true,
      };
    case 'open_request_completed':
      return {
        tone: 'info',
        title: `${outcome.recipientName} just signed their W-9`,
        body: 'The W-9 that was already out was signed while we were replacing it, so no new request went out. There is nothing more to send.',
        offerReplace: false,
      };
    case 'in_flight':
      return {
        tone: 'info',
        title: `A W-9 request for ${outcome.recipientName} is already being sent`,
        body: 'Give it a moment, then check the Requests page before sending again.',
        offerReplace: false,
      };
    case 'rejected':
      if (outcome.error === 'staff_email') {
        return {
          tone: 'danger',
          title: 'That is a Welcome Home Properties address',
          body: `Send the W-9 to the ${who}'s own email, not a staff address.`,
          offerReplace: false,
        };
      }
      if (outcome.error === 'vendor_requires_id') {
        return {
          tone: 'danger',
          title: 'Pick the vendor from AppFolio',
          body: 'A vendor W-9 has to be tied to the vendor’s AppFolio record, so choose them from the search results.',
          offerReplace: false,
        };
      }
      if (outcome.error === 'unknown_recipient') {
        return {
          tone: 'danger',
          title: `That ${who} is not in AppFolio's list anymore`,
          body: `They could not be found in AppFolio, or they are hidden there. Search again and pick the ${who} from the results.`,
          offerReplace: false,
        };
      }
      return {
        tone: 'danger',
        title: 'Check the details',
        body:
          outcome.field === 'email'
            ? 'The email was not accepted. Check it and send again.'
            : outcome.field === 'displayName'
              ? 'The name was not accepted. Check it and send again.'
              : 'Something in the name or email was not accepted. Check both and send again.',
        offerReplace: false,
      };
    case 'not_enabled':
      return {
        tone: 'info',
        title: 'Requesting a W-9 is not available yet',
        body:
          kind === 'vendor'
            ? 'Vendor W-9 requests have not been turned on yet.'
            : 'Owner W-9 requests have not been turned on yet.',
        offerReplace: false,
      };
    case 'unavailable':
      return {
        tone: 'warning',
        title: 'W-9 requests are not available right now',
        body: outcome.message || 'Something W-9 requests depend on is not set up yet. Try again later.',
        offerReplace: false,
      };
    case 'failed':
    default: {
      const f = outcome as { message?: string; outcomeUnknown?: boolean };
      if (f.outcomeUnknown) {
        return {
          tone: 'warning',
          title: 'We could not confirm the W-9 request went out',
          body: f.message || 'Check the Requests page before trying again, so the recipient does not get two.',
          offerReplace: false,
        };
      }
      return {
        tone: 'danger',
        title: 'The W-9 request did not go out',
        body: f.message || 'Try again in a moment.',
        offerReplace: false,
      };
    }
  }
}


/**
 * Plain-language toast text for a failed EPMA initiate that involved the existing-
 * owner match (workstream A's codes). Returns null for any other error so the page
 * keeps its existing handling. Names only — never an AppFolio id.
 */
export function ownerMatchInitiateError(body: unknown): string | null {
  const b = (body ?? {}) as { error?: unknown; owners?: unknown };
  if (b.error === 'unknown_owner') {
    const names = Array.isArray(b.owners)
      ? b.owners.filter((n): n is string => typeof n === 'string' && n.trim() !== '')
      : [];
    const who = names.length ? names.join(', ') : 'One of the linked owners';
    return `${who} could not be found in AppFolio anymore. Choose "Not this owner" to send as a new owner, or pick the owner again.`;
  }
  if (b.error === 'owner_lookup_failed') {
    return 'We could not check the linked owners against AppFolio just now. Try again in a moment.';
  }
  return null;
}

// ---------------------------------------------------------------------------
// §13.1 — Existing vs new owner on the EPMA form
// ---------------------------------------------------------------------------

/** GET /api/owners/similar candidate: an owner match plus the plain-language reason. */
export type SimilarOwner = OwnerMatch & { reason: string };

/** Which kind of owner staff said a roster row is (§13.1). Default: new. */
export type EpmaOwnerKind = 'existing' | 'new';

/** The name fields a similar-owner check reads. */
export interface SimilarNameFields {
  ownerType: 'individual' | 'entity';
  firstName: string;
  lastName: string;
  entityName: string;
}

/**
 * The query for GET /api/owners/similar, or null when there is not enough name
 * to ask about. An individual is checked by first + last name; an entity by its
 * company name only — its signer is a person AppFolio does not hold as the owner,
 * and matching the signer would flag their personal owner record as the entity.
 */
export function similarQueryFor(
  row: SimilarNameFields,
): { first?: string; last?: string; company?: string } | null {
  // The route needs a last name or a company, and refuses any part over 100
  // characters (400 invalid_query) — don't ask what it will refuse.
  const MAX = 100;
  if (row.ownerType === 'entity') {
    const company = row.entityName.trim();
    return company.length >= 2 && company.length <= MAX ? { company } : null;
  }
  const first = row.firstName.trim();
  const last = row.lastName.trim();
  if (!last || last.length + first.length < 2 || last.length > MAX || first.length > MAX) return null;
  return first ? { first, last } : { last };
}

/**
 * A stable key for "the name as typed" — the similar check's answer, and staff's
 * "It's a different owner", both belong to exactly this name. Case and spacing
 * don't change it; any real edit does.
 */
export function similarNameKey(row: SimilarNameFields): string {
  const q = similarQueryFor(row);
  if (!q) return '';
  const norm = (s: string | undefined) => (s ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
  return [row.ownerType, norm(q.company), norm(q.first), norm(q.last)].join('|');
}

export interface SimilarRowState extends SimilarNameFields {
  ownerKind: EpmaOwnerKind;
  appfolioOwnerId?: string | undefined;
  /** The last similar check, keyed by the name it ran for. */
  similar?: { key: string; candidates: SimilarOwner[] } | undefined;
  /** The name key staff answered "It's a different owner" for. */
  similarDismissedKey?: string | undefined;
}

/**
 * The possible-existing-owner warning for a NEW owner card: the candidates for
 * the name as it stands now, unless staff already said it is a different owner
 * for this very name. Nothing for an existing / linked row, a stale answer, or
 * no candidates (which is also what a switched-off endpoint returns).
 */
export function similarCandidatesFor(row: SimilarRowState): SimilarOwner[] {
  if (row.ownerKind !== 'new' || row.appfolioOwnerId) return [];
  const key = similarNameKey(row);
  if (!key || !row.similar || row.similar.key !== key) return [];
  if (row.similarDismissedKey === key) return [];
  return row.similar.candidates.slice(0, 5);
}

/** The server's reason, shown only when it carries no id; else nothing. */
export function similarReasonText(c: Pick<SimilarOwner, 'reason'>): string {
  const r = (c.reason ?? '').trim();
  return r && !OPAQUE_ID_RE.test(r) ? r : '';
}

/**
 * The override is offered only when EVERY owner is an existing, linked AppFolio
 * owner (§13.1): then nothing more is collected unless staff tick it, and ticking
 * it collects contact details only. A mixed roster always gets the full intake.
 */
export function allOwnersExisting(
  rows: ReadonlyArray<{ ownerKind: EpmaOwnerKind; appfolioOwnerId?: string | undefined }>,
): boolean {
  return rows.length > 0 && rows.every((r) => r.ownerKind === 'existing' && !!r.appfolioOwnerId);
}

export const CONTACT_ONLY_OVERRIDE_LABEL = 'Collect owner details anyway';
export const CONTACT_ONLY_OVERRIDE_HELPER =
  "They'll confirm contact details and backup contacts. Banking and year-end tax setup stay as they are in AppFolio. An owner with no W-9 on file is asked for one.";

/**
 * Is owner matching switched on? Read from one probe of GET /api/owners/lookup
 * with an EMPTY email (lib/ownerSyncClient.probeOwnerMatch). The route says so
 * explicitly (API 35295d9): off → 200 { matches: [], enabled: false }; on → the
 * empty email is refused, 400 { error: 'invalid_email', enabled: true }.
 *   • an explicit `enabled` boolean (on a 200 or that 400) decides;
 *   • without one (an older API): a 200 = off (the switched-off route answered
 *     before validating), a 400 = on (it got as far as refusing the email);
 *   • anything else (404, 5xx, network) = off: today's form, never a half-feature.
 */
export function ownerMatchAvailableFrom(
  probe: { status: number; body: unknown } | null,
): boolean {
  if (!probe) return false;
  const ok = probe.status >= 200 && probe.status < 300;
  if (!ok && probe.status !== 400) return false;
  const enabled = (probe.body as { enabled?: unknown } | null)?.enabled;
  if (typeof enabled === 'boolean') return enabled;
  return probe.status === 400;
}
